import { useQuery } from '@apollo/react-hooks';
import {
  endOfMonth,
  isSameDay,
  max,
  startOfDay,
  endOfDay,
  startOfHour,
  startOfMonth,
  subDays,
} from 'date-fns';
import { Box, StackView } from 'localmed-core';
import React, { useEffect, useRef, useState } from 'react';
import { DatePicker } from '../../../shared/components/DiComponents';
import { LoadingOverlay } from '../../../shared/components/DiLayout/DiGenericLoading';
import { DILM_CLIENT_CONTEXT, LM_CLIENT_CONTEXT } from '../../../shared/constants';
import parseDate, { parseDateNonNullable } from '../../../shared/utils/parseDate';
import { IOpening } from '../../../types';
import { ISchedulingValues } from '../../pages/SchedulingPage/components/schedulingWizard';
import nextOpeningQuery from './queries/SchedulingNextOpeningQuery.graphql';
import timeSeriesQuery from './queries/SchedulingOpeningsTimeSeriesQuery.graphql';
import SchedulingOpeningSection from './SchedulingOpeningSection';

interface Props {
  values: ISchedulingValues;
  setValue: (name: string, value: any) => void;
  unavailableOpenings?: Array<IOpening>;
}

export default function SchedulingTimeSelectionCalendar({
  values,
  setValue,
  unavailableOpenings,
}: Props) {
  const highLightedDaysRef = useRef<any>([]);

  const {
    selectedDate,
    // queryProvider used to differenciate between the current provider and the provider used to query openings data
    queryProvider,
    reasonForVisit,
    office,
    scheduleAfter,
  } = values;

  // We need to use a somewhat consistent date for "now" or else the nextOpeningQuery
  // will trigger an infinite loop because the `after` param will constantly change.
  // The openings service will apply the openings buffer anyway so using a date
  // slightly in the past will be fine.
  const now = startOfHour(new Date());
  const parsedScheduleAfter = (scheduleAfter && parseDate(scheduleAfter)) || now;

  const currentDate = max([selectedDate, parsedScheduleAfter]);
  const initialSelectedDate = max([now, parsedScheduleAfter]);

  const [currentMonth, setCurrentMonth] = useState(currentDate);

  // next Opening Query
  const nextOpeningQueryVars = queryProvider?.id
    ? { providerId: queryProvider?.id }
    : { officeId: office?.id };

  const { data: nextOpeningData } = useQuery(nextOpeningQuery, {
    variables: {
      ...nextOpeningQueryVars,
      reasonForVisitId: reasonForVisit?.id,
      after: currentDate.toISOString(),
    },
    skip: !office?.id || !reasonForVisit?.id || currentDate >= initialSelectedDate,
    context: LM_CLIENT_CONTEXT,
  });

  // Time Series Query
  const timeSeriesQueryVars = queryProvider?.id
    ? { providerId: queryProvider?.id }
    : { officeId: office?.id };

  const { data: timeSeriesData, loading: timeSeriesLoading } = useQuery(timeSeriesQuery, {
    variables: {
      ...timeSeriesQueryVars,
      reasonForVisitId: reasonForVisit?.id,
      timezone: office?.timeZoneCode,
      startDate: startOfDay(startOfMonth(currentMonth)).toISOString(),
      endDate: endOfDay(endOfMonth(currentMonth)).toISOString(),
    },
    context: DILM_CLIENT_CONTEXT,
  });
  const { lmOpeningsStatsTimeSeries: { series = [] } = {} } = timeSeriesData || {};

  // Move to next opening date if initial selected Date is not nextOpening Date
  useEffect(() => {
    if (nextOpeningData && nextOpeningData.nextOpening) {
      const { nextOpening } = nextOpeningData;
      const initial = startOfDay(selectedDate);
      const nextOpeningDate = startOfDay(parseDateNonNullable(nextOpening.time));
      if (nextOpeningDate && nextOpeningDate > initial) {
        setValue('selectedDate', parseDate(nextOpening.time));
        setCurrentMonth(nextOpeningDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOpeningData]);

  if (!timeSeriesLoading) {
    highLightedDaysRef.current = series
      .filter((serie) => serie.count > 0)
      .map((serie) => parseDate(serie.startDate));
  }
  const daysWithOpenings = highLightedDaysRef.current;

  return (
    <StackView
      spacing={{ _: 'lg', lg: 'lg' }}
      direction={{ _: 'column', lg: 'row' }}
      alignItems={{ _: 'center', lg: 'flex-start' }}
      justifyContent={{ _: 'center', lg: 'space-between' }}
      mt="md"
    >
      <Box position="relative">
        {timeSeriesLoading && <LoadingOverlay />}
        <DatePicker
          selectedDays={max([selectedDate, parsedScheduleAfter])}
          initialMonth={currentDate}
          disabledDays={[
            { before: max([new Date(), subDays(parsedScheduleAfter, 1)]) },
            (date) => !daysWithOpenings.find((highlightedDate) => isSameDay(highlightedDate, date)),
          ]}
          fromMonth={max([new Date(), parsedScheduleAfter])}
          modifiers={{
            highlighted: daysWithOpenings,
          }}
          onChange={(selectedDay) => {
            setValue('opening', null);
            setValue('selectedDate', selectedDay);
          }}
          onMonthChange={(value) => {
            highLightedDaysRef.current = [];
            setCurrentMonth(value);
          }}
          ml={{ _: 'none', lg: '-0.5em' }}
          p="none"
        />
      </Box>

      <Box width="100%" display="flex" justifyContent={{ _: 'center', lg: 'flex-end' }}>
        <SchedulingOpeningSection
          values={values}
          setValue={setValue}
          currentDate={currentDate}
          nextOpening={nextOpeningData?.nextOpening}
          datePickerLoading={timeSeriesLoading}
          unavailableOpenings={unavailableOpenings}
        />
      </Box>
    </StackView>
  );
}
